import { useCreation } from 'ahooks';
import { ConfigProvider, theme } from 'antd';
import ukUA from 'antd/locale/uk_UA';
import { AxiosError } from 'axios';
import { FC } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { useLocation } from 'react-router';

import { AppMessage, Spinner } from '@/common/components';
import { useAppRouter, useAuthState, useFetchSessionUser, useInitAppSocket, useStaticRouter } from '@/common/hooks';
import { StaticRoutes } from '@/common/interfaces';
import { CommonLayout } from '@/common/layouts';
import { mutationErrorHandler, queryErrorHandler } from '@/common/utils';

import { useInitAddressChangerSocket } from '@/modules/AddressChanger';

import './App.scss';

export const App: FC = () => {
  const queryClient = useCreation(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnMount: true,
            retryOnMount: false,
            refetchOnWindowFocus: false,
            onError: async (error) => {
              if (error instanceof AxiosError) {
                if (error.code === 'ERR_NETWORK') {
                  throw new Error('Network error');
                }
              }
            },
          },
          mutations: {
            retry: false,
          },
        },
        queryCache: new QueryCache({
          onError: queryErrorHandler,
        }),
        mutationCache: new MutationCache({
          onError: mutationErrorHandler,
        }),
      }),
    [],
  );

  const { setAuthingState } = useAuthState();

  return (
    <ConfigProvider
      locale={ukUA}
      theme={{
        cssVar: true,
        // algorithm: theme.darkAlgorithm,
        algorithm: theme.defaultAlgorithm,
        token: {
          borderRadius: 4,
        },
      }}
    >
      <AppMessage>
        <QueryClientProvider client={queryClient}>
          <AppInner />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </AppMessage>
    </ConfigProvider>
  );
};

const AppInner: FC = () => {
  const location = useLocation();

  const { isLoading: isFetchSessionUserLoading } = useFetchSessionUser();

  const { isAuthed, sessionUser } = useAuthState();

  const {
    router: { common: CommonRouter, drawer: DrawerRouter },
  } = useAppRouter();

  const staticRouter = useStaticRouter();

  useInitAppSocket();
  // useInitAddressChangerSocket();

  if (location.pathname.startsWith(StaticRoutes.StaticPrefix)) {
    return staticRouter;
  } else {
    return (
      <>
        {isFetchSessionUserLoading || !sessionUser ? (
          <Spinner />
        ) : (
          <CommonLayout commonRouter={CommonRouter} drawerRouter={DrawerRouter} />
        )}
      </>
    );
  }
};
